import React, { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useHistory } from 'react-router-dom';

import {
    Button,
    IconButton,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Toolbar,
    Paper,
    TablePagination,
    Menu,
    MenuItem,
    Box,
} from '@material-ui/core';
import i18n from 'i18n-js';
import ArrowBackIcon from '@material-ui/icons/ArrowBack';
import PictureAsPdfIcon from '@material-ui/icons/PictureAsPdf';
import PrintIcon from '@material-ui/icons/Print';

import { pagingOptions } from 'constants/arrays';
import { useStyles } from './styles';
import globalStore from 'store/globalStore';
import agent from 'api/agent';
import { useAppSelector } from 'index';
import { formatDateIntl } from 'helpers/datetimeHelper';

export interface ClosingReport {
    id: string;
    dateFrom: number;
    dateTo: number;
    totalAmount: number;
    totalTax: number;
    type: ClosingReportType;
}

export enum ClosingReportType {
    X = 'X',
    Z = 'Z',
}

export default function Closings() {
    const classes = useStyles();
    const dispatch = useDispatch();
    const history = useHistory();

    const language = useAppSelector((state) => state.global.language);
    const [count, setCount] = useState(0);
    const [reports, setReports] = useState<ClosingReport[]>([]);
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(pagingOptions[page]);
    const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);

    async function createNewReport(type: ClosingReportType) {
        dispatch(globalStore.actionCreators.showSpiner());
        setAnchorEl(null);
        try {
            const newReport = await agent.Payments.CreateNewClosingReport({ closingType: type });
            if (newReport) {
                await agent.Payments.GenerateClosinfReportPDF(newReport.id);
                setReports([newReport, ...reports]);
            }
        } catch (error) {
            dispatch(globalStore.actionCreators.showToaster('error', error));
        } finally {
            dispatch(globalStore.actionCreators.hideSpiner());
        }
    }

    async function downloadReportPDF(id: string) {
        dispatch(globalStore.actionCreators.showSpiner());
        try {
            await agent.Payments.GenerateClosinfReportPDF(id);
        } catch (error) {
            dispatch(globalStore.actionCreators.showToaster('error', error));
        } finally {
            dispatch(globalStore.actionCreators.hideSpiner());
        }
    }

    async function printReportPDF(id: string) {
        dispatch(globalStore.actionCreators.showSpiner());
        try {
            await agent.Payments.GenerateClosinfReportPDFForPrint(id);
        } catch (error) {
            dispatch(globalStore.actionCreators.showToaster('error', error));
        } finally {
            dispatch(globalStore.actionCreators.hideSpiner());
        }
    }

    useEffect(() => {
        const getReports = async () => {
            dispatch(globalStore.actionCreators.showSpiner());
            try {
                const closings = await agent.Payments.GetClosingsReports(page, rowsPerPage);
                if (closings) {
                    setReports(closings.list);
                    setCount(closings.count);
                }
            } catch (error) {
                dispatch(globalStore.actionCreators.showToaster('error', error));
            } finally {
                dispatch(globalStore.actionCreators.hideSpiner());
            }
        };
        getReports();
    }, [page, rowsPerPage]);
    return (
        <Box className={classes.root}>
            <Toolbar
                style={{
                    paddingBottom: '10px',
                }}
            >
                <Box display="flex" flexDirection="row">
                    <IconButton
                        aria-label="delete"
                        size="small"
                        className={classes.iconButton}
                        onClick={() => history.goBack()}
                    >
                        <ArrowBackIcon fontSize="inherit" />
                    </IconButton>
                </Box>

                <Button
                    variant="contained"
                    color="primary"
                    style={{ marginRight: 5, width: '212px' }}
                    onClick={(event) => setAnchorEl(event.currentTarget)}
                >
                    {i18n.t('common.generateClosingReport').toUpperCase()}
                </Button>
                <Menu
                    anchorEl={anchorEl}
                    keepMounted
                    open={Boolean(anchorEl)}
                    onClose={() => setAnchorEl(null)}
                    anchorOrigin={{
                        vertical: 'bottom',
                        horizontal: 'left',
                    }}
                    transformOrigin={{
                        vertical: 'top',
                        horizontal: 'left',
                    }}
                    PaperProps={{
                        style: {
                            minWidth: '212px',
                            marginTop: '40px',
                        },
                    }}
                >
                    <MenuItem onClick={() => createNewReport(ClosingReportType.X)}>
                        {`X - ${i18n.t('common.closing')}`}
                    </MenuItem>
                    <MenuItem onClick={() => createNewReport(ClosingReportType.Z)}>
                        {`Z - ${i18n.t('common.closing')}`}
                    </MenuItem>
                </Menu>
            </Toolbar>
            <Paper>
                <TableContainer id="container" style={{ maxHeight: '90%' }}>
                    <Table stickyHeader className={classes.root} aria-label="simple table">
                        <TableHead>
                            <TableRow>
                                <TableCell
                                    className={classes.tableCellHeader}
                                    style={{ paddingLeft: 18, width: 100 }}
                                >
                                    <Box className={classes.tableCellHeaderContainer}>
                                        {i18n.t('common.type')}
                                    </Box>
                                </TableCell>
                                <TableCell className={classes.tableCellHeader} width={'22%'}>
                                    <Box className={classes.tableCellHeaderContainer}>
                                        {i18n.t('common.start')}
                                    </Box>
                                </TableCell>
                                <TableCell className={classes.tableCellHeader} width={'22%'}>
                                    <Box className={classes.tableCellHeaderContainer}>
                                        {i18n.t('common.end')}
                                    </Box>
                                </TableCell>
                                <TableCell className={classes.tableCellHeader} width={'18%'}>
                                    <Box className={classes.tableCellHeaderContainer}>
                                        {i18n.t('common.total')}
                                    </Box>
                                </TableCell>
                                <TableCell className={classes.tableCellHeader} width={'18%'}>
                                    <Box className={classes.tableCellHeaderContainer}>
                                        {i18n.t('paymentDetails.vat')}
                                    </Box>
                                </TableCell>
                                <TableCell className={classes.tableCellHeader}>
                                    <div
                                        style={{ padding: 16, width: 100 }}
                                        className={classes.tableCellHeaderContainer}
                                    >
                                        {i18n.t('common.actions')}
                                    </div>
                                </TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {reports.map((report) => (
                                <TableRow key={report.id}>
                                    <TableCell align={'left'}>
                                        <Box component="span"> {report.type}</Box>
                                    </TableCell>
                                    <TableCell>
                                        <Box component="span">
                                            {formatDateIntl(report.dateFrom, language)}
                                        </Box>
                                    </TableCell>
                                    <TableCell align={'left'}>
                                        <Box component="span">
                                            {formatDateIntl(report.dateTo, language)}
                                        </Box>
                                    </TableCell>
                                    <TableCell align={'left'}>
                                        <Box component="span">{report.totalAmount}</Box>
                                    </TableCell>
                                    <TableCell align={'left'}>
                                        <Box component="span">{report.totalTax}</Box>
                                    </TableCell>
                                    <TableCell align={'left'}>
                                        <IconButton
                                            aria-label="delete"
                                            size="small"
                                            className={classes.iconButton}
                                            style={{ marginLeft: 10 }}
                                            onClick={() => downloadReportPDF(report.id)}
                                        >
                                            <PictureAsPdfIcon fontSize="medium" color="primary" />
                                        </IconButton>
                                        <IconButton
                                            aria-label="delete"
                                            size="small"
                                            className={classes.iconButton}
                                            style={{ marginLeft: 10 }}
                                            onClick={() => printReportPDF(report.id)}
                                        >
                                            <PrintIcon fontSize="medium" color="primary" />
                                        </IconButton>
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </TableContainer>

                <TablePagination
                    rowsPerPageOptions={pagingOptions}
                    component="div"
                    count={count}
                    rowsPerPage={rowsPerPage}
                    labelRowsPerPage={i18n.t('common.rowsPerPage')}
                    page={page}
                    onPageChange={(_, page) => setPage(page)}
                    onRowsPerPageChange={(event) => setRowsPerPage(+event.target.value)}
                />
            </Paper>
        </Box>
    );
}
